import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AngleRight, EllipsisH } from '@mehilainen/mds-icons';
import { Gray100, Gray200, Gray300, Gray600, Gray700, Gray900, Primary500, } from '@mehilainen/mds-tokens/colors';
import { spacing } from '@mehilainen/mds-tokens/spacing';
import { baseTypography } from '@mehilainen/mds-tokens/typography';
import { Box, Stack, styled, Typography, } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { forwardRef } from 'react';
import { shouldForwardProp } from '../mds-shared';
const titleSizeMap = {
    medium: {
        fontSize: baseTypography.size[400].rem,
        fontWeight: baseTypography.weight.Regular,
        lineHeight: baseTypography.height.Medium,
    },
    small: {
        fontSize: baseTypography.size[300].rem,
        fontWeight: baseTypography.weight.Regular,
        lineHeight: baseTypography.height.Medium,
    },
};
const descriptionSizeMap = {
    medium: {
        fontSize: baseTypography.size[300].rem,
        fontWeight: baseTypography.weight.Regular,
        lineHeight: baseTypography.height.Medium,
    },
    small: {
        fontSize: baseTypography.size[200].rem,
        fontWeight: baseTypography.weight.Regular,
        lineHeight: baseTypography.height.Medium,
    },
};
const textContainerSizeMap = {
    medium: {
        padding: `${spacing[500].px} ${spacing[500].px} ${spacing[500].px} 0`,
    },
    small: {
        padding: `${spacing[550].px} ${spacing[500].px} ${spacing[550].px} 0`,
    },
};
const OuterContainer = styled(Box)(() => ({
    listStyleType: 'none',
    [`&:last-of-type .${listBlockClasses.content}`]: {
        // Hide bottom border from the last ListBlock item
        borderColor: 'transparent',
    },
}));
const InnerContainer = styled(Box)(() => ({
    boxSizing: 'border-box',
    display: 'flex',
    padding: `0 ${spacing[500].px}`,
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    gap: spacing[500].px,
    width: '100%',
    // Counter default styles
    '&[href], &[role="button"], &[role="menuitem"]': {
        textDecoration: 'none',
        color: 'inherit',
        background: 'none',
        border: 'none',
        font: 'inherit',
        textAlign: 'inherit',
        margin: 0,
    },
    '&:hover': {
        backgroundColor: Gray100,
    },
    '&:active': {
        backgroundColor: Gray200,
    },
    '&:focus': {
        backgroundColor: 'initial',
    },
}));
const StartIcon = styled(Stack)(() => ({
    '& > svg': {
        color: Primary500,
        fontSize: baseTypography.size.icon[400].px,
    },
}));
const ContentContainer = styled(Box, { shouldForwardProp })(() => ({
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${Gray300}`,
}));
const TextContainer = styled(Box, { shouldForwardProp })(({ $size }) => (Object.assign({ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: spacing[200].px, flex: '1 0 0' }, textContainerSizeMap[$size])));
const Title = styled(Typography, { shouldForwardProp })(({ $size }) => (Object.assign({}, titleSizeMap[$size])));
const Description = styled(Typography, { shouldForwardProp })(({ $size }) => (Object.assign({}, descriptionSizeMap[$size])));
const EndIcon = styled(Stack)(() => ({
    '& > svg': {
        color: Gray700,
        fontSize: baseTypography.size.icon[500].px,
    },
}));
let idCounter = 0;
// TODO: Replace with React.useId() when it is available
function generateUniqueId(prefix) {
    idCounter += 1;
    return `${prefix}-${idCounter}`;
}
const defaultProps = {
    endIcon: (_jsx(AngleRight, {})),
    size: 'medium',
    startIcon: (_jsx(EllipsisH, {})),
};
export const listBlockClasses = {
    root: 'MdsListBlock-root',
    content: 'MdsListBlock-content',
};
/**
 * ListBlock component for showing list items (blocks)
 *
 * Usage:
 *   <ListBlock
 *     title="Item Title"
 *     description="Optional description of the item"
 *     startIcon={<CustomIcon />}
 *     endIcon={<AngleRight />}
 *     size="medium"
 *     onClick={() => handleClick()}
 *     href="https://example.com"
 *     target="_blank"
 *     sx={{ custom: 'styles' }}
 *   />
 *
 * Props:
 * - title: string - The main text of the list item
 * - description?: string - Optional secondary text
 * - startIcon?: React.ReactNode | false - Icon to display at the start of the item. Defaults to EllipsisH. Use false to remove the icon.
 * - endIcon?: React.ReactNode | false - Icon to display at the end of the item. Defaults to AngleRight. Use false to remove the icon.
 * - size?: 'medium' | 'small' - Controls the overall size of the component, affecting text size and component dimensions (defaults to 'medium')
 * - onClick?: function - Handler for click events on the list item. If provided (and href is not), the component renders as a button.
 * - sx?: SxProps - Custom styles using MUI's sx prop
 * - href?: string - URL to navigate to when the list item is clicked. If provided, the component renders as an anchor tag.
 * - target?: string - Specifies where to open the linked document (e.g., "_blank" for a new tab). Only applicable when href is provided.
 * - TitleProps?: TypographyProps - Additional props for the title Typography component
 * - DescriptionProps?: TypographyProps - Additional props for the description Typography component
 */
export const ListBlock = forwardRef(({ DescriptionProps, TitleProps, description, endIcon = defaultProps.endIcon, href, onClick, size = defaultProps.size, startIcon = defaultProps.startIcon, sx, target, title, role, }, ref) => {
    const ElementType = href ? 'a' : 'button';
    const labelId = title ? generateUniqueId('list-block-label') : undefined;
    const descriptionId = description
        ? generateUniqueId('list-block-desc')
        : undefined;
    const outerRole = role === 'menuitem' ? 'none' : 'listitem';
    const innerRole = href ? undefined : role || 'button';
    if (href && onClick) {
        // eslint-disable-next-line no-console
        console.warn('Both href and onClick provided to ListBlock. href will take precedence.');
    }
    if (href && role) {
        // eslint-disable-next-line no-console
        console.warn('Both href and role provided to ListBlock. href will take precedence.');
    }
    return (_jsx(OuterContainer, { as: "li", role: outerRole, children: _jsxs(InnerContainer, { as: ElementType, className: listBlockClasses.root, 
            // @ts-expect-error: href is not a valid prop for button
            href: href, target: href ? target : undefined, onClick: href ? undefined : onClick, role: innerRole, tabIndex: 0, "aria-labelledby": labelId, "aria-describedby": descriptionId, sx: sx, ref: ref, children: [startIcon && _jsx(StartIcon, { "aria-hidden": "true", children: startIcon }), _jsxs(ContentContainer, { "aria-hidden": "true", className: listBlockClasses.content, children: [_jsxs(TextContainer, { "$size": size, children: [title && (_jsx(Title, Object.assign({ "$size": size, color: Gray900 }, TitleProps, { children: title }))), description && (_jsx(Description, Object.assign({ "$size": size, color: Gray600 }, DescriptionProps, { children: description })))] }), endIcon && _jsx(EndIcon, { children: endIcon })] }), title && (_jsx(Box, { id: labelId, sx: visuallyHidden, children: title })), description && (_jsx(Box, { id: descriptionId, sx: visuallyHidden, children: description }))] }) }));
});
